/**
 * The browser entry point: the real ports, handed to `startApp`. Nothing else
 * belongs here — it is the one file no unit test can load (it touches
 * `document` at import), listed in dev/coverage/neverLoaded.ts.
 */
import './styles/globals.css';
import { toast } from 'sonner';
import { startApp } from './app/start.js';
import { realSrpLib } from './auth/cognito.js';
import type { StoragePort } from './auth/session.js';
import { registerServiceWorker, type ServiceWorkerHandle } from './sw/register.js';

/** This build's commit and commit time when the build names them (`vite.config.ts`' `define`, D37 §4.1; null: the release build). */
declare const __ACP_WEB_BUILD__: { readonly commit: string; readonly builtAt: string | null } | null;

/** The build's time as epoch ms, or null (none, or one that does not parse: an unknown age, never a page that cannot start). */
function builtAtMsOf(iso: string | null): number | null {
  const ms = iso === null ? Number.NaN : Date.parse(iso);
  return Number.isNaN(ms) ? null : ms;
}

/** The service worker, once registered: what the reload prompt of a `426 upgrade-required` asks for the current build. */
let serviceWorker: ServiceWorkerHandle | null = null;
const UPGRADE_TOAST_ID = 'acp-upgrade-required';

/** localStorage, guarded: absent or throwing (private mode, blocked site data) reads as empty and ignores writes. */
const storage: StoragePort = {
  get: (key) => {
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set: (key, value) => {
    try {
      window.localStorage.setItem(key, value);
    } catch {
      // Storage unavailable: the session lasts as long as the tab.
    }
  },
  remove: (key) => {
    try {
      window.localStorage.removeItem(key);
    } catch {
      // As above.
    }
  },
};

const container = document.getElementById('root');
if (container === null) throw new Error('index.html has no #root element');

void startApp({
  container,
  origin: window.location.origin,
  // A `timeoutMs` (the health probes') aborts the request, so a dead host leaves no hung sockets behind.
  fetch: (url, { timeoutMs, ...init }) => window.fetch(url, timeoutMs === undefined ? init : { ...init, signal: AbortSignal.timeout(timeoutMs) }),
  fetchJson: (path) => window.fetch(path, { headers: { accept: 'application/json' }, cache: 'no-store' }),
  storage,
  onStorageChange: (listener) => window.addEventListener('storage', listener),
  nowMs: () => Date.now(),
  after: (ms, fn) => {
    const id = window.setTimeout(fn, ms);
    return () => window.clearTimeout(id);
  },
  srpLib: realSrpLib,
  root: document.documentElement,
  // Chrome's Local Network Access permission (connectivity/localNetwork.ts); absent in browsers without the API.
  permissions: 'permissions' in navigator ? navigator.permissions : null,
  // D35: a field a newer server added is dropped, not refused — this build may be older than the API it talks to.
  responses: 'tolerant',
  // D37 §4.1: who this is, and the prompt when a host says it is too old (the floor): the current build, then reload.
  clientBuild: __ACP_WEB_BUILD__ === null ? null : { commit: __ACP_WEB_BUILD__.commit, builtAtMs: builtAtMsOf(__ACP_WEB_BUILD__.builtAt) },
  onUpgradeRequired: (message) => {
    toast.warning(message, {
      id: UPGRADE_TOAST_ID,
      duration: Number.POSITIVE_INFINITY,
      action: {
        label: 'Reload',
        onClick: () => {
          if (serviceWorker === null) window.location.reload();
          else void serviceWorker.updateNow();
        },
      },
    });
  },
});

// The app shell's service worker (src/sw/): production builds only — under `vite dev` it would cache modules HMR
// replaces. It keeps the UI loadable while a node restarts; a new build waits for the operator's "Reload".
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  void registerServiceWorker({
    container: navigator.serviceWorker,
    onUpdateReady: (apply) => {
      toast('A new version of Norna is ready', { duration: Number.POSITIVE_INFINITY, action: { label: 'Reload', onClick: apply } });
    },
    reload: () => window.location.reload(),
    every: (ms, fn) => {
      window.setInterval(fn, ms);
    },
    reportError: (error) => {
      console.warn('[acp] service worker:', error);
    },
  }).then((handle) => {
    serviceWorker = handle;
  });
}
